import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BreadcrumbNavDialogComponent } from '../../../shared/components/header/breadcrumb-nav-dialog/breadcrumb-nav-dialog.component';
import { GlobalSearch } from '../../../shared/components/header/global-search/global-search';
import {
  isEditableKeyboardTarget,
  isInteractiveKeyboardTarget,
} from '../../utils/keyboard-target.utils';
import { DialogService, DynamicDialogRef } from '../dialog';
import { RoleService } from '../role/role.service';

@Injectable({
  providedIn: 'root',
})
/**
 * Service to manage keyboard shortcuts and application-wide accessibility bindings.
 */
export class ShortcutService {
  /** Internal router reference. */
  private router = inject(Router);
  /** Internal dialogService reference. */
  private dialogService = inject(DialogService);
  /** Role checks for create shortcuts. */
  private role = inject(RoleService);
  /** Material dialogs already on screen. Space must not open another one. */
  private readonly matDialog = inject(MatDialog);
  /** Active global-search dialog ref, if open. */
  private dialogRef: DynamicDialogRef | undefined | null;
  /** Active breadcrumb dialog ref, so Space opens it only once. */
  private breadcrumbDialogRef: DynamicDialogRef | undefined | null;
  /** True after the window listener is registered. */
  private listening = false;

  /**
   * Registers the single window keydown listener for application shortcuts.
   * A second call does nothing.
   */
  public init(): void {
    if (this.listening || typeof window === 'undefined') {
      return;
    }
    this.listening = true;
    window.addEventListener('keydown', this.onWindowKeydown);
    window.addEventListener('keyup', this.onWindowKeyup);
  }

  /**
   * Closes the breadcrumb modal when Space is released.
   */
  private readonly onWindowKeyup = (event: KeyboardEvent): void => {
    if (event.code !== 'Space' || event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    this.closeBreadcrumbDialog();
  };

  /**
   * Window keydown listener. Holding Space opens the breadcrumb modal when it is safe.
   */
  private readonly onWindowKeydown = (event: KeyboardEvent): void => {
    if (this.consumeBreadcrumbShortcut(event)) {
      return;
    }

    const target = event.target;
    const isTyping =
      target instanceof HTMLElement &&
      (target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable);

    const isCmdOrCtrl = event.metaKey || event.ctrlKey;
    const isShift = event.shiftKey;
    const key = event.key?.toLowerCase();
    if (!key) return;

    if (isTyping && !(isCmdOrCtrl && !isShift && key === 'k')) {
      return;
    }

    if (isCmdOrCtrl && isShift && key === 's') {
      event.preventDefault();
      this.handleAction('new');
      return;
    }

    if (isCmdOrCtrl && isShift && key === 'l') {
      event.preventDefault();
      this.handleAction('list');
      return;
    }

    if (isCmdOrCtrl && !isShift && key === 'k') {
      event.preventDefault();
      this.openGlobalSearch();
    }
  };

  /**
   * Opens the breadcrumb modal for the duration of an unmodified Space press.
   * @param event - Window keydown event
   * @returns True when this was a Space key event
   */
  private consumeBreadcrumbShortcut(event: KeyboardEvent): boolean {
    if (
      event.code !== 'Space' ||
      event.shiftKey ||
      event.ctrlKey ||
      event.metaKey ||
      event.altKey
    ) {
      return false;
    }
    if (
      isEditableKeyboardTarget(event.target) ||
      isInteractiveKeyboardTarget(event.target)
    ) {
      return true;
    }
    if (this.matDialog.openDialogs.length > 0 && !this.breadcrumbDialogRef) {
      return true;
    }
    event.preventDefault();
    if (event.repeat || this.breadcrumbDialogRef) {
      return true;
    }
    this.openBreadcrumbDialog();
    return true;
  }

  /**
   * Closes the breadcrumb modal opened by the current Space press.
   */
  private closeBreadcrumbDialog(): void {
    this.breadcrumbDialogRef?.close();
  }

  /**
   * Opens the current breadcrumb trail in the shared dialog service.
   */
  private openBreadcrumbDialog(): void {
    this.breadcrumbDialogRef = this.dialogService.open(
      BreadcrumbNavDialogComponent,
      {
        header: '',
        showHeader: false,
        width: 'max-content',
        height: 'auto',
        baseZIndex: 10000,
        dismissableMask: false,
        modal: false,
        closeOnEscape: true,
        closable: false,
        styleClass: 'breadcrumb-nav-dialog',
        contentStyle: {
          padding: '0',
          overflow: 'visible',
          background: 'transparent',
        },
      },
    );

    this.breadcrumbDialogRef?.onClose.subscribe(() => {
      this.breadcrumbDialogRef = null;
    });
  }

  /**
   * Handle action.
   *
   * @param type - The type parameter
   * @returns The Observable result
   */
  private handleAction(type: 'new' | 'list') {
    const url = this.router.url.toLowerCase();

    if (url.includes('shipment')) {
      if (type === 'new') {
        if (!this.role.isPermitted('customsShipment.create')) {
          return;
        }
        this.router.navigate(['/shipments/all'], {
          queryParams: { openPopup: 'import' },
        });
      } else {
        this.router.navigate(['/shipments/all']);
      }
    } else if (url.includes('declaration')) {
      if (type === 'new') {
        if (!this.role.isPermitted('declaration.create')) {
          return;
        }
        this.router.navigate(['/declarations/new']);
      } else {
        this.router.navigate(['/declarations']);
      }
    }
  }

  /**
   * Open global search.
   * @returns The Observable result
   */
  public openGlobalSearch() {
    // Already open?
    if (this.dialogRef) {
      this.dialogRef.close();
      this.dialogRef = null;
    }

    this.dialogRef = this.dialogService.open(GlobalSearch, {
      width: '700px',
      height: 'auto',
      baseZIndex: 10000,
      dismissableMask: true,
      modal: true,
      closeOnEscape: true,
      closable: true,
      styleClass: 'custom-header',
      style: {
        'border-radius': '10px',
        position: 'fixed',
        top: '60px',
        left: '50%',
        transform: 'translateX(-50%)',
      },
    });

    if (this.dialogRef) {
      this.dialogRef.onClose.subscribe(() => {
        this.dialogRef = null;
      });
    }
  }
}
